import { FC, Fragment, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { RangeTuple } from 'fuse.js';
import { TravellerWithCardsDTO } from '@/api/trafikportalen';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { cn } from '@/helpers/classnames';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import {
  getFullName,
  getMatchingTravellerSearchCards,
  type MatchRanges,
  type TravellerResultGroup,
  type TravellerSearchCardItem,
  type TravellerSearchCardKind,
  type TravellerSearchHighlights,
} from '@/helpers/travellerSearch';
import {
  IconAppEmail,
  IconGraduationCap,
  IconOrganisation,
  IconSmartcard,
  IconSmartphoneDevice,
  IconWallet,
  IconXMarkCircle,
  IconYouthCard,
} from '@/icons';
import Button from '@/components/core/Button';

/** Merges overlapping/adjacent ranges so highlights render as continuous runs. */
const mergeRanges = (ranges: MatchRanges): RangeTuple[] => {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: RangeTuple[] = [];

  sorted.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1] + 1) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  });

  return merged;
};

interface HighlightedTextProps {
  text: string;
  ranges?: MatchRanges;
}

/** Renders `text`, wrapping the matched ranges in a highlighted `<mark>`. */
const HighlightedText: FC<HighlightedTextProps> = ({ text, ranges }) => {
  if (!ranges || ranges.length === 0) {
    return <>{text}</>;
  }

  const segments: ReactNode[] = [];
  let cursor = 0;

  mergeRanges(ranges).forEach(([start, end], index) => {
    const safeStart = Math.max(cursor, Math.min(start, text.length));
    const safeEnd = Math.max(safeStart, Math.min(end + 1, text.length));

    if (safeStart > cursor) {
      segments.push(text.slice(cursor, safeStart));
    }
    if (safeEnd > safeStart) {
      segments.push(
        <mark key={index} className="rounded-[2px] bg-yellow-200 text-text-950">
          {text.slice(safeStart, safeEnd)}
        </mark>,
      );
    }
    cursor = safeEnd;
  });

  if (cursor < text.length) {
    segments.push(text.slice(cursor));
  }

  return <>{segments}</>;
};

interface SearchCardRowsProps {
  cards: TravellerSearchCardItem[];
  highlights?: TravellerSearchHighlights;
}

const SEARCH_CARD_GROUPS: {
  kind: TravellerSearchCardKind;
  Icon: typeof IconSmartcard;
}[] = [
  { kind: 'travelcard', Icon: IconSmartcard },
  { kind: 'walletcard', Icon: IconWallet },
  { kind: 'schoolcard', Icon: IconGraduationCap },
];

const getSearchCardLabel = (
  kind: TravellerSearchCardKind,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  switch (kind) {
    case 'travelcard':
      return formatMessage({ defaultMessage: 'Resekort' });
    case 'walletcard':
      return formatMessage({ defaultMessage: 'Tjänsteresa på kort' });
    case 'schoolcard':
      return formatMessage({ defaultMessage: 'Skolkort' });
  }
};

const SearchCardRows: FC<SearchCardRowsProps> = ({ cards, highlights }) => {
  const { formatMessage } = useIntl();
  const studentsDisabled = useStudentsDisabled();
  const cardGroups = studentsDisabled
    ? SEARCH_CARD_GROUPS.filter(({ kind }) => kind !== 'schoolcard')
    : SEARCH_CARD_GROUPS;

  return (
    <>
      {cardGroups.map(({ kind, Icon }) => {
        const groupCards = cards.filter((card) => card.kind === kind);
        if (groupCards.length === 0) {
          return null;
        }

        const label = getSearchCardLabel(kind, formatMessage);

        return (
          <div
            key={kind}
            className="flex items-center gap-2 text-text-700"
            title={label}
          >
            <span className="sr-only">{label}: </span>
            <Icon className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">
              {groupCards.map((card, index) => (
                <Fragment key={`${kind}-${card.card_serial}-${index}`}>
                  {index > 0 && ', '}
                  <HighlightedText
                    text={card.card_serial}
                    ranges={highlights?.cards.get(card.card_serial)}
                  />
                </Fragment>
              ))}
            </span>
          </div>
        );
      })}
    </>
  );
};

interface DeletedAtLabelProps {
  deletedAt: string;
}

const DeletedAtLabel: FC<DeletedAtLabelProps> = ({ deletedAt }) => (
  <span
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-red-100 px-2 py-0.5 text-sm text-red-800"
    title={formatDateTime(deletedAt)}
  >
    <IconXMarkCircle className="size-4 shrink-0" aria-hidden="true" />
    <FormattedMessage
      defaultMessage="Borttagen {date}"
      values={{ date: formatDate(deletedAt) }}
    />
    <span className="sr-only">
      <FormattedMessage defaultMessage="Kan inte öppnas." />
    </span>
  </span>
);

interface SearchResultCardProps {
  deleted: boolean;
  focused: boolean;
  onOpen: () => void;
  className: string;
  children: ReactNode;
}

/** A deleted traveller stays visible, but other endpoints exclude them. */
const SearchResultCard: FC<SearchResultCardProps> = ({
  deleted,
  focused,
  onOpen,
  className,
  children,
}) => {
  const focusClassName = focused
    ? 'shadow-[0_0_0_3px_inset_white] ring-2 ring-inset ring-primary-500'
    : undefined;

  if (deleted) {
    return (
      <div
        aria-current={focused ? 'true' : undefined}
        className={cn(
          'flex rounded-md border border-background-400 bg-background-50',
          className,
          focusClassName,
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <Button
      aria-current={focused ? 'true' : undefined}
      onClick={onOpen}
      className={cn(className, focusClassName)}
    >
      {children}
    </Button>
  );
};

interface TravellerIdentityProps {
  fullName: string | null;
  organizationName?: string;
  email?: string;
  bobcatPrivateEmail?: string;
  nationalIdentityNumber?: string;
  phone?: string;
  cards: TravellerSearchCardItem[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  highlights?: TravellerSearchHighlights;
}

const TravellerIdentity: FC<TravellerIdentityProps> = ({
  fullName,
  organizationName,
  email,
  bobcatPrivateEmail,
  nationalIdentityNumber,
  phone,
  cards,
  createdAt,
  updatedAt,
  deletedAt,
  highlights,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="flex w-full min-w-0 flex-col gap-1 text-left">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className="min-w-0 truncate font-medium"
          title={formatMessage({ defaultMessage: 'Namn' })}
        >
          {fullName ? (
            <HighlightedText
              text={fullName}
              ranges={highlights?.fullName.get(fullName)}
            />
          ) : (
            <i>
              <FormattedMessage defaultMessage="Anonym resenär" />
            </i>
          )}
        </span>
        {deletedAt && <DeletedAtLabel deletedAt={deletedAt} />}
      </div>
      {organizationName && (
        <span
          className="flex items-center gap-1 text-text-700"
          title={formatMessage({ defaultMessage: 'Organisation' })}
        >
          <span className="sr-only">
            <FormattedMessage defaultMessage="Organisation" />:{' '}
          </span>
          <IconOrganisation className="size-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{organizationName}</span>
        </span>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-text-700">
        {email && (
          <span
            className="min-w-0 truncate"
            title={formatMessage({ defaultMessage: 'E-post' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="E-post" />:{' '}
            </span>
            <HighlightedText
              text={email}
              ranges={highlights?.email.get(email)}
            />
          </span>
        )}
        {nationalIdentityNumber && (
          <span
            className="flex items-center whitespace-nowrap"
            title={formatMessage({ defaultMessage: 'Personnummer' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="Personnummer" />:{' '}
            </span>
            <IconYouthCard
              className="mr-1 size-4 shrink-0"
              aria-hidden="true"
            />
            <HighlightedText
              text={nationalIdentityNumber}
              ranges={highlights?.nationalIdentityNumber.get(
                nationalIdentityNumber,
              )}
            />
          </span>
        )}
        {bobcatPrivateEmail && (
          <span
            className="flex min-w-0 items-center"
            title={formatMessage({
              defaultMessage: 'E-post i appen / Mina sidor',
            })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
              :{' '}
            </span>
            <IconAppEmail className="mr-1 size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">
              <HighlightedText
                text={bobcatPrivateEmail}
                ranges={highlights?.bobcatPrivateEmail.get(bobcatPrivateEmail)}
              />
            </span>
          </span>
        )}
        {phone && (
          <span
            className="flex items-center whitespace-nowrap"
            title={formatMessage({ defaultMessage: 'Telefon' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="Telefon" />:{' '}
            </span>
            <IconSmartphoneDevice
              className="mr-1 size-4 shrink-0"
              aria-hidden="true"
            />
            <HighlightedText
              text={phone}
              ranges={highlights?.phone.get(phone)}
            />
          </span>
        )}
      </div>
      <SearchCardRows cards={cards} highlights={highlights} />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-700">
        <span title={formatDateTime(createdAt)}>
          <FormattedMessage defaultMessage="Skapad" /> {formatDate(createdAt)}
        </span>
        <span aria-hidden="true">·</span>
        <span title={formatDateTime(updatedAt)}>
          <FormattedMessage defaultMessage="Uppdaterad" />{' '}
          {formatDate(updatedAt)}
        </span>
      </div>
    </div>
  );
};

interface TravellerSearchOrgButtonProps {
  record: TravellerWithCardsDTO;
  organizationName: string;
  /**
   * Show the record's own email/national identity number. The group does this
   * when the field differs between the person's organizations and is therefore
   * not shown once in the shared header.
   */
  showEmail: boolean;
  showNationalIdentityNumber: boolean;
  highlights?: TravellerSearchHighlights;
  query: string;
  /** Highlights the button when navigated to via the keyboard. */
  hasManualFocus: boolean;
  onClick: () => void;
}

/**
 * One organization within a grouped sysadmin result. Because the same person
 * can hold different details per organization, each button carries that
 * organization's name plus the traveller's name, app email ("E-post i appen / Mina sidor"),
 * phone, cards and created/updated timestamps as recorded there — plus the
 * email and/or national identity number when those differ between the person's
 * organizations. A deleted record shows when it was removed and cannot be opened.
 */
const TravellerSearchOrgButton: FC<TravellerSearchOrgButtonProps> = ({
  record,
  organizationName,
  showEmail,
  showNationalIdentityNumber,
  highlights,
  query,
  hasManualFocus,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const fullName = getFullName(record);
  const {
    email,
    national_identity_number,
    phone,
    bobcat_private_email,
    created_at,
    updated_at,
    deleted_at,
  } = record.traveller;
  const cards = getMatchingTravellerSearchCards(record, query);

  return (
    <SearchResultCard
      deleted={Boolean(deleted_at)}
      focused={hasManualFocus}
      onOpen={onClick}
      className="h-fit w-auto min-w-64 flex-1 items-start justify-start gap-3 p-3 text-left font-normal"
    >
      <IconOrganisation className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className="min-w-0 truncate font-medium"
          title={formatMessage({ defaultMessage: 'Organisation' })}
        >
          <span className="sr-only">
            <FormattedMessage defaultMessage="Organisation" />:{' '}
          </span>
          {organizationName}
        </span>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className="min-w-0 truncate text-text-700"
            title={formatMessage({ defaultMessage: 'Namn' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="Namn" />:{' '}
            </span>
            {fullName ? (
              <HighlightedText
                text={fullName}
                ranges={highlights?.fullName.get(fullName)}
              />
            ) : (
              <i>
                <FormattedMessage defaultMessage="Anonym resenär" />
              </i>
            )}
          </span>
          {deleted_at && <DeletedAtLabel deletedAt={deleted_at} />}
        </div>
        {showEmail && email && (
          <span
            className="min-w-0 truncate text-text-700"
            title={formatMessage({ defaultMessage: 'E-post' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="E-post" />:{' '}
            </span>
            <HighlightedText
              text={email}
              ranges={highlights?.email.get(email)}
            />
          </span>
        )}
        {showNationalIdentityNumber && national_identity_number && (
          <span
            className="flex items-center whitespace-nowrap text-text-700"
            title={formatMessage({ defaultMessage: 'Personnummer' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="Personnummer" />:{' '}
            </span>
            <IconYouthCard
              className="mr-1 size-4 shrink-0"
              aria-hidden="true"
            />
            <HighlightedText
              text={national_identity_number}
              ranges={highlights?.nationalIdentityNumber.get(
                national_identity_number,
              )}
            />
          </span>
        )}
        {bobcat_private_email && (
          <span
            className="flex min-w-0 items-center gap-1 text-text-700"
            title={formatMessage({
              defaultMessage: 'E-post i appen / Mina sidor',
            })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
              :{' '}
            </span>
            <IconAppEmail className="size-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">
              <HighlightedText
                text={bobcat_private_email}
                ranges={highlights?.bobcatPrivateEmail.get(
                  bobcat_private_email,
                )}
              />
            </span>
          </span>
        )}
        {phone && (
          <span
            className="flex items-center whitespace-nowrap text-text-700"
            title={formatMessage({ defaultMessage: 'Telefon' })}
          >
            <span className="sr-only">
              <FormattedMessage defaultMessage="Telefon" />:{' '}
            </span>
            <IconSmartphoneDevice
              className="mr-1 size-4 shrink-0"
              aria-hidden="true"
            />
            <HighlightedText
              text={phone}
              ranges={highlights?.phone.get(phone)}
            />
          </span>
        )}
        <SearchCardRows cards={cards} highlights={highlights} />
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-700">
          <span title={formatDateTime(created_at)}>
            <FormattedMessage defaultMessage="Skapad" />{' '}
            {formatDate(created_at)}
          </span>
          <span aria-hidden="true">·</span>
          <span title={formatDateTime(updated_at)}>
            <FormattedMessage defaultMessage="Uppdaterad" />{' '}
            {formatDate(updated_at)}
          </span>
        </div>
      </div>
    </SearchResultCard>
  );
};

interface TravellerSearchResultGroupProps {
  group: TravellerResultGroup;
  organizationNameById: Map<string, string>;
  onImpersonate: (organizationId: string, travellerId: string) => void;
  highlights?: TravellerSearchHighlights;
  query: string;
  /** Index of the keyboard-highlighted organization, or -1 for none. */
  activeOrgIndex?: number;
}

/**
 * Result for sysadmins. When the traveller exists in a single organization
 * there is nothing to group, so it renders as one flat impersonation card.
 * When the same person exists in several organizations, email and national
 * identity number are shared and shown once at the top while the per-org
 * details live on one card each, since the same person can differ between
 * organizations. A deleted record is marked and cannot be opened.
 */
export const TravellerSearchResultGroup: FC<
  TravellerSearchResultGroupProps
> = ({
  group,
  organizationNameById,
  onImpersonate,
  highlights,
  query,
  activeOrgIndex = -1,
}) => {
  const { formatMessage } = useIntl();

  if (group.records.length === 1) {
    const [record] = group.records;
    const { traveller } = record;
    const organizationName =
      organizationNameById.get(traveller.organization_id) ??
      formatMessage({ defaultMessage: 'Okänd organisation' });

    return (
      <SearchResultCard
        deleted={Boolean(traveller.deleted_at)}
        focused={activeOrgIndex === 0}
        onOpen={() => onImpersonate(traveller.organization_id, traveller.id)}
        className="h-fit w-full items-start justify-start gap-4 p-4 font-normal"
      >
        <TravellerIdentity
          fullName={getFullName(record)}
          organizationName={organizationName}
          email={traveller.email}
          bobcatPrivateEmail={traveller.bobcat_private_email}
          nationalIdentityNumber={traveller.national_identity_number}
          phone={traveller.phone}
          cards={getMatchingTravellerSearchCards(record, query)}
          createdAt={traveller.created_at}
          updatedAt={traveller.updated_at}
          deletedAt={traveller.deleted_at}
          highlights={highlights}
        />
      </SearchResultCard>
    );
  }

  return (
    <div
      role="group"
      aria-label={
        group.email ??
        group.nationalIdentityNumber ??
        formatMessage({ defaultMessage: 'Resenär i flera organisationer' })
      }
      className="flex h-fit w-full flex-col gap-3 rounded-md border border-background-400 p-4"
    >
      {(group.email || group.nationalIdentityNumber) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {group.email && (
            <span
              className="min-w-0 truncate font-medium"
              title={formatMessage({ defaultMessage: 'E-post' })}
            >
              <span className="sr-only">
                <FormattedMessage defaultMessage="E-post" />:{' '}
              </span>
              <HighlightedText
                text={group.email}
                ranges={highlights?.email.get(group.email)}
              />
            </span>
          )}
          {group.nationalIdentityNumber && (
            <span
              className="flex items-center gap-1 whitespace-nowrap text-text-700"
              title={formatMessage({ defaultMessage: 'Personnummer' })}
            >
              <span className="sr-only">
                <FormattedMessage defaultMessage="Personnummer" />:{' '}
              </span>
              <IconYouthCard className="size-4 shrink-0" aria-hidden="true" />
              <HighlightedText
                text={group.nationalIdentityNumber}
                ranges={highlights?.nationalIdentityNumber.get(
                  group.nationalIdentityNumber,
                )}
              />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {group.records.map((record, index) => (
          <TravellerSearchOrgButton
            key={record.traveller.id}
            record={record}
            organizationName={
              organizationNameById.get(record.traveller.organization_id) ??
              formatMessage({ defaultMessage: 'Okänd organisation' })
            }
            showEmail={!group.email}
            showNationalIdentityNumber={!group.nationalIdentityNumber}
            highlights={highlights}
            query={query}
            hasManualFocus={index === activeOrgIndex}
            onClick={() =>
              onImpersonate(
                record.traveller.organization_id,
                record.traveller.id,
              )
            }
          />
        ))}
      </div>
    </div>
  );
};
